import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Button from '../components/ui/Button';
import Card from '../components/ui/Card';
import Badge from '../components/ui/Badge';

const PricingPage = () => {
  const navigate = useNavigate();
  const [isAnnual, setIsAnnual] = useState(false);

  const pricingTiers = [
    {
      id: 'free',
      name: 'Trader',
      tagline: 'Perfect for getting started',
      monthlyPrice: 0,
      annualPrice: 0,
      cta: 'Start Free',
      features: [
        { text: 'Buy and sell domains', included: true },
        { text: 'Basic AI valuations', included: true },
        { text: 'Public portfolio', included: true },
        { text: 'Community access', included: true },
        { text: 'Standard transaction fees (8%)', included: true },
        { text: 'Make/receive trade offers', included: true },
        { text: 'Early access to listings', included: false },
        { text: 'Advanced analytics', included: false },
      ]
    },
    {
      id: 'pro',
      name: 'Pro Trader',
      tagline: 'For serious domain investors',
      monthlyPrice: 49,
      annualPrice: 39,
      annualSavings: 120,
      cta: 'Start Pro Trial',
      highlighted: true,
      features: [
        { text: 'Everything in Free', included: true },
        { text: 'Early access to listings (30 min)', included: true },
        { text: 'Advanced analytics dashboard', included: true },
        { text: 'Reduced transaction fees (5%)', included: true },
        { text: 'Priority trade matching', included: true },
        { text: 'Market trend predictions', included: true },
        { text: 'Pro badge on profile', included: true },
        { text: '10 custom alerts', included: true },
      ]
    },
    {
      id: 'elite',
      name: 'Elite Trader',
      tagline: 'For domain trading professionals',
      monthlyPrice: 199,
      annualPrice: 159,
      annualSavings: 480,
      cta: 'Start Elite Trial',
      features: [
        { text: 'Everything in Pro', included: true },
        { text: 'Even lower fees (3%)', included: true },
        { text: '60 minutes early access', included: true },
        { text: 'AI-powered trade recommendations', included: true },
        { text: 'Unlimited custom alerts', included: true },
        { text: 'White-glove concierge service', included: true },
        { text: 'Exclusive Elite badge', included: true },
        { text: 'Portfolio insurance (up to $10K)', included: true },
      ]
    },
  ];

  const faqs = [
    {
      question: 'Can I switch plans at any time?',
      answer: 'Yes! You can upgrade or downgrade your plan at any time. Changes take effect immediately and we\'ll prorate any payments.'
    },
    {
      question: 'What payment methods do you accept?',
      answer: 'We accept all major credit cards, PayPal, and cryptocurrency payments.'
    },
    {
      question: 'Are there any hidden fees?',
      answer: 'No hidden fees! The only costs are your monthly subscription and the transaction fees listed above.'
    },
    {
      question: 'What happens to my listings if I downgrade?',
      answer: 'All your listings remain active. You\'ll just lose access to premium features like early listing access and advanced analytics.'
    },
    {
      question: 'Do you offer refunds?',
      answer: 'We offer a 14-day money-back guarantee for all paid plans. No questions asked.'
    },
    {
      question: 'Can I get a discount for annual billing?',
      answer: 'Yes! You save 20% when you choose annual billing on Pro or Elite plans.'
    }
  ];

  const PricingCard = ({ tier }) => {
    const price = isAnnual ? tier.annualPrice : tier.monthlyPrice;
    const savings = isAnnual && tier.annualSavings;

    return (
      <Card
        className={`relative p-8 ${
          tier.highlighted ? 'border-2 border-blue-600 scale-105 shadow-xl' : ''
        }`}
      >
        {tier.highlighted && (
          <div className="absolute -top-4 left-1/2 -translate-x-1/2">
            <Badge variant="primary" size="lg">
              Most Popular
            </Badge>
          </div>
        )}

        <div className="text-center mb-6">
          <h3 className="text-2xl font-bold mb-2">{tier.name}</h3>
          <p className="text-gray-600 text-sm mb-4">{tier.tagline}</p>
          <div>
            <span className="text-4xl font-bold">${price}</span>
            <span className="text-gray-600">/month</span>
          </div>
          {savings && (
            <p className="text-sm text-green-600 mt-2">Save ${savings}/year</p>
          )}
        </div>

        <Button
          fullWidth
          className="mb-6"
          variant={tier.highlighted ? 'primary' : 'outline'}
        >
          {tier.cta}
        </Button>

        <div className="space-y-3">
          {tier.features.map((feature, index) => (
            <div key={index} className="flex items-start gap-3">
              {feature.included ? (
                <span className="text-green-500 text-xl mt-0.5">✓</span>
              ) : (
                <span className="text-gray-300 text-xl mt-0.5">✗</span>
              )}
              <span className={feature.included ? '' : 'text-gray-400'}>
                {feature.text}
              </span>
            </div>
          ))}
        </div>
      </Card>
    );
  };

  return (
    <div className="min-h-screen py-20">
      <div className="container mx-auto px-4">
        <div className="text-center mb-16">
          <h1 className="text-5xl font-bold mb-4">Simple, Transparent Pricing</h1>
          <p className="text-xl text-gray-600">
            Choose the plan that fits your trading style
          </p>

          {/* Annual/Monthly toggle */}
          <div className="flex items-center justify-center gap-4 mt-8">
            <span className={!isAnnual ? 'font-bold' : 'text-gray-600'}>
              Monthly
            </span>
            <button
              onClick={() => setIsAnnual(!isAnnual)}
              className={`relative w-14 h-7 rounded-full transition-colors ${
                isAnnual ? 'bg-blue-600' : 'bg-gray-300'
              }`}
            >
              <div
                className={`absolute top-1 left-1 w-5 h-5 bg-white rounded-full transition-transform ${
                  isAnnual ? 'translate-x-7' : ''
                }`}
              />
            </button>
            <span className={isAnnual ? 'font-bold' : 'text-gray-600'}>
              Annual{' '}
              <Badge variant="success" size="sm">
                Save 20%
              </Badge>
            </span>
          </div>
        </div>

        {/* Pricing cards grid */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-6xl mx-auto mb-20">
          {pricingTiers.map(tier => (
            <PricingCard key={tier.id} tier={tier} />
          ))}
        </div>

        {/* Transaction fee comparison */}
        <div className="mb-20">
          <h2 className="text-3xl font-bold text-center mb-8">
            Transaction Fee Comparison
          </h2>
          <Card className="max-w-2xl mx-auto p-8">
            <table className="w-full">
              <thead>
                <tr className="border-b">
                  <th className="text-left py-3">Tier</th>
                  <th className="text-right py-3">Fee</th>
                  <th className="text-right py-3">On $10K Sale</th>
                </tr>
              </thead>
              <tbody>
                <tr className="border-b">
                  <td className="py-3 font-medium">Trader</td>
                  <td className="text-right">8%</td>
                  <td className="text-right text-red-600 font-bold">-$800</td>
                </tr>
                <tr className="border-b">
                  <td className="py-3 font-medium">Pro</td>
                  <td className="text-right">5%</td>
                  <td className="text-right text-orange-600 font-bold">-$500</td>
                </tr>
                <tr>
                  <td className="py-3 font-medium">Elite</td>
                  <td className="text-right">3%</td>
                  <td className="text-right text-green-600 font-bold">-$300</td>
                </tr>
              </tbody>
            </table>
            <p className="text-sm text-gray-600 mt-4 text-center">
              Elite members save $500 per $10,000 sale compared to Free tier
            </p>
            <div className="mt-6 p-4 bg-blue-50 rounded-lg">
              <p className="text-sm text-center">
                💡 <strong>Pro Tip:</strong> If you sell just $1,000/month, Pro tier pays for itself in saved fees!
              </p>
            </div>
          </Card>
        </div>

        {/* All Features Comparison */}
        <div className="mb-20">
          <h2 className="text-3xl font-bold text-center mb-8">
            Complete Feature Comparison
          </h2>
          <Card className="max-w-5xl mx-auto p-8 overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b">
                  <th className="text-left py-3">Feature</th>
                  <th className="text-center py-3">Trader</th>
                  <th className="text-center py-3">Pro</th>
                  <th className="text-center py-3">Elite</th>
                </tr>
              </thead>
              <tbody>
                <tr className="border-b">
                  <td className="py-3">Transaction Fee</td>
                  <td className="text-center">8%</td>
                  <td className="text-center">5%</td>
                  <td className="text-center">3%</td>
                </tr>
                <tr className="border-b">
                  <td className="py-3">Early Listing Access</td>
                  <td className="text-center">-</td>
                  <td className="text-center">30 min</td>
                  <td className="text-center">60 min</td>
                </tr>
                <tr className="border-b">
                  <td className="py-3">Custom Alerts</td>
                  <td className="text-center">-</td>
                  <td className="text-center">10</td>
                  <td className="text-center">Unlimited</td>
                </tr>
                <tr className="border-b">
                  <td className="py-3">AI Valuations</td>
                  <td className="text-center">Basic</td>
                  <td className="text-center">Advanced</td>
                  <td className="text-center">Premium</td>
                </tr>
                <tr className="border-b">
                  <td className="py-3">Portfolio Analytics</td>
                  <td className="text-center">-</td>
                  <td className="text-center">✓</td>
                  <td className="text-center">✓</td>
                </tr>
                <tr className="border-b">
                  <td className="py-3">Priority Support</td>
                  <td className="text-center">-</td>
                  <td className="text-center">✓</td>
                  <td className="text-center">✓</td>
                </tr>
                <tr className="border-b">
                  <td className="py-3">Concierge Service</td>
                  <td className="text-center">-</td>
                  <td className="text-center">-</td>
                  <td className="text-center">✓</td>
                </tr>
                <tr>
                  <td className="py-3">Portfolio Insurance</td>
                  <td className="text-center">-</td>
                  <td className="text-center">-</td>
                  <td className="text-center">Up to $10K</td>
                </tr>
              </tbody>
            </table>
          </Card>
        </div>

        {/* FAQ */}
        <div className="max-w-3xl mx-auto">
          <h2 className="text-3xl font-bold text-center mb-12">
            Frequently Asked Questions
          </h2>
          <div className="space-y-6">
            {faqs.map((faq, index) => (
              <Card key={index} className="p-6">
                <h3 className="font-bold mb-2">{faq.question}</h3>
                <p className="text-gray-600">{faq.answer}</p>
              </Card>
            ))}
          </div>
        </div>

        {/* Bottom CTA */}
        <div className="text-center mt-20">
          <Card className="max-w-2xl mx-auto p-12 bg-gradient-to-br from-blue-50 to-purple-50">
            <h2 className="text-3xl font-bold mb-4">Still have questions?</h2>
            <p className="text-gray-600 mb-6">
              Our team is here to help you choose the right plan
            </p>
            <div className="flex gap-4 justify-center">
              <Button onClick={() => navigate('/demo')}>Try Demo</Button>
              <Button variant="outline">Contact Sales</Button>
            </div>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default PricingPage;
